import type { RunStatus } from "../status.ts";

export interface Claim {
  path: string;
  agent: number;
  wp: number;
  status: RunStatus;
  /** Other agents claiming the same path. */
  overlapWith?: readonly number[];
}

/** The phone form of the codebase map: claimed paths grouped by agent, in join order. */
export function claimsByAgent(claims: readonly Claim[]): Array<{ agent: number; claims: Claim[] }> {
  const groups = new Map<number, Claim[]>();
  for (const c of claims) groups.set(c.agent, [...(groups.get(c.agent) ?? []), c]);
  return [...groups].sort((a, b) => a[0] - b[0]).map(([agent, list]) => ({ agent, claims: list }));
}

/** J and K move through events, clamped at the ends. */
export function moveSelection(current: number, delta: 1 | -1, length: number): number {
  if (length === 0) return -1;
  return Math.min(length - 1, Math.max(0, current + delta));
}

export interface RunKeyHandlers {
  next(): void;
  previous(): void;
  open(): void;
  replay(): void;
  approve?(): void;
}

/** True when the key press belongs to the page, not to a field the person is typing in. */
export function shouldHandleKey(target: { tagName?: string; isContentEditable?: boolean } | null): boolean {
  if (!target) return true;
  const tag = (target.tagName ?? "").toLowerCase();
  return !(tag === "input" || tag === "textarea" || tag === "select" || target.isContentEditable);
}

/**
 * Live run shortcuts: J and K move through events, Enter opens, R replays, A approves when an approval is pending.
 * Returns an unbind function.
 */
export function bindRunKeys(doc: { addEventListener(t: "keydown", fn: (e: KeyboardEvent) => void): void; removeEventListener(t: "keydown", fn: (e: KeyboardEvent) => void): void }, handlers: RunKeyHandlers): () => void {
  const onKey = (e: KeyboardEvent): void => {
    if (e.metaKey || e.ctrlKey || e.altKey || !shouldHandleKey(e.target as Element | null)) return;
    const key = e.key.toLowerCase();
    if (key === "j") handlers.next();
    else if (key === "k") handlers.previous();
    else if (key === "enter") {
      const tag = ((e.target as Element | null)?.tagName ?? "").toLowerCase();
      if (tag === "button" || tag === "a") return; // let a focused control keep its own Enter
      handlers.open();
    }
    else if (key === "r") handlers.replay();
    else if (key === "a" && handlers.approve) handlers.approve();
    else return;
    e.preventDefault();
  };
  doc.addEventListener("keydown", onKey);
  return () => doc.removeEventListener("keydown", onKey);
}
